import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Chip,
  Divider,
  Alert,
  Autocomplete,
  MenuItem,
  CircularProgress,
  Grow,
  useTheme,
} from "@mui/material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import Swal from "sweetalert2";
import { tokens } from "../../../theme";
import {
  fetchJsonDynamic,
  executeAnyMutation,
} from "../../../data/DynamicServiceNew/jsondynamicservice";
import { formatCurrency, parseErrorMessage } from "../stageBoardShared";
import { customerservice } from "../../../config";

const GrowTransition = React.forwardRef(function GrowTransition(props, ref) {
  return <Grow ref={ref} {...props} />;
});

const SEARCH_DEBOUNCE_MS = 450;
const MIN_SEARCH_LENGTH = 3;
const INELIGIBLE_STATUSES = ["REPOSSESSED", "DISPOSED"];

const customerLabel = (customer) => {
  const name = `${customer.FirstName || ""} ${customer.OtherNames || ""}`.trim();
  return `${name || "Unnamed customer"} — ${customer.MobileNumber || "no phone"}`;
};

const InitiateDefaultDialog = ({ open, onClose, onSuccess, base_url }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [searchInput, setSearchInput] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [customerAssets, setCustomerAssets] = useState([]);
  const [lienBlockedAssets, setLienBlockedAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState("");

  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Debounced customer search by phone number (or name/ID).
  useEffect(() => {
    const term = searchInput.trim();
    if (term.length < MIN_SEARCH_LENGTH) {
      setCustomerOptions([]);
      return;
    }
    setSearchLoading(true);
    const handle = setTimeout(async () => {
      const response = await fetchJsonDynamic(
        "dynamicCustomerSearch",
        "customer",
        { searchTerm: term, approval: 2, isAssetCustomer: 1 },
        customerservice.uri,
      );
      let results = [];
      if (response && !response.error && response.results) {
        try {
          const parsed = JSON.parse(response.results);
          results = Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          results = [];
        }
      }
      setCustomerOptions(results);
      setSearchLoading(false);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [searchInput, base_url]);

  // Load the selected customer's collateral assets.
  useEffect(() => {
    if (!selectedCustomer) {
      setCustomerAssets([]);
      setLienBlockedAssets([]);
      setSelectedAssetId("");
      return;
    }
    (async () => {
      setAssetsLoading(true);
      setSelectedAssetId("");
      setLienBlockedAssets([]);
      const response = await fetchJsonDynamic(
        "collateralAssetByColumnValues",
        "asset",
        { customerId: Number(selectedCustomer.Id) },
        base_url,
      );
      let rows = [];
      if (response && !response.error && response.results) {
        try {
          const parsed = JSON.parse(response.results);
          rows = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          rows = [];
        }
      }
      const candidates = rows.filter((a) => !INELIGIBLE_STATUSES.includes(a.AssetStatus));

      // Assets that require a lien can only be marked as defaulted once that
      // lien is REGISTERED with the registry — verify each one before offering it.
      const eligible = [];
      const blocked = [];

      await Promise.all(
        candidates.map(async (asset) => {
          if (!asset.RequireLien) {
            eligible.push(asset);
            return;
          }
          try {
            const lienResponse = await fetchJsonDynamic(
              "lienRegistrationsByColumnValues",
              "lien",
              { assetId: asset.Id },
              base_url,
            );
            const liens = lienResponse?.results ? JSON.parse(lienResponse.results) : [];
            const hasRegisteredLien = (Array.isArray(liens) ? liens : []).some(
              (lien) => lien.LienStatus === "REGISTERED",
            );
            if (hasRegisteredLien) {
              eligible.push(asset);
            } else {
              blocked.push(asset);
            }
          } catch (error) {
            blocked.push(asset);
          }
        }),
      );

      setCustomerAssets(eligible);
      setLienBlockedAssets(blocked);
      setAssetsLoading(false);
    })();
  }, [selectedCustomer, base_url]);

  const resetAndClose = () => {
    if (submitting) return;
    setSearchInput("");
    setCustomerOptions([]);
    setSelectedCustomer(null);
    setCustomerAssets([]);
    setLienBlockedAssets([]);
    setSelectedAssetId("");
    setTouched(false);
    setErrorMessage(null);
    onClose();
  };

  const handleSubmit = async () => {
    setTouched(true);
    setErrorMessage(null);
    if (!selectedCustomer || !selectedAssetId) return;

    setSubmitting(true);
    try {
      const response = await executeAnyMutation(
        "updateCollateralAssetStatus",
        { id: Number(selectedAssetId), status: "REPOSSESSED" },
        "asset",
        false,
        base_url,
      );

      if (response?.error) {
        setErrorMessage(parseErrorMessage(response.error));
        return;
      }

      const asset = customerAssets.find((a) => a.Id === Number(selectedAssetId));
      Swal.fire({
        icon: "success",
        title: "Asset Marked as Defaulted",
        text: `${selectedCustomer.FirstName || "The customer"}'s ${asset?.AssetTypeName || "asset"} has been moved to Defaulted Assets.`,
      });
      onSuccess();
      resetAndClose();
    } catch (error) {
      setErrorMessage("An error occurred while initiating the default. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      fullWidth
      maxWidth="sm"
      TransitionComponent={GrowTransition}
      keepMounted={false}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          background: "linear-gradient(135deg, #1766A2 0%, #2196F3 100%)",
          color: "#fff",
        }}
      >
        <ReportProblemIcon />
        Initiate Defaulted Asset
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
        )}

        <Autocomplete
          options={customerOptions}
          value={selectedCustomer}
          inputValue={searchInput}
          onInputChange={(e, value, reason) => {
            if (reason === "input") setSearchInput(value);
          }}
          onChange={(e, value) => setSelectedCustomer(value)}
          getOptionLabel={(option) => (option ? customerLabel(option) : "")}
          isOptionEqualToValue={(option, value) => option.Id === value.Id}
          loading={searchLoading}
          filterOptions={(options) => options}
          noOptionsText={
            searchInput.trim().length < MIN_SEARCH_LENGTH
              ? "Type at least 3 digits of the phone number"
              : "No matching customers found"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Customer by Phone Number"
              placeholder="e.g. 0723759937"
              error={touched && !selectedCustomer}
              helperText={touched && !selectedCustomer ? "Select a customer to continue" : " "}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ mb: selectedCustomer ? 2 : 0 }}
        />

        {selectedCustomer && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
            <Chip
              label={`${selectedCustomer.FirstName || ""} ${selectedCustomer.OtherNames || ""}`.trim()}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip label={selectedCustomer.MobileNumber || "No phone"} size="small" variant="outlined" />
            {selectedCustomer.IdNoOrPassport && (
              <Chip label={selectedCustomer.IdNoOrPassport} size="small" variant="outlined" />
            )}
          </Stack>
        )}

        {selectedCustomer && (
          <>
            <Divider sx={{ mb: 2, borderColor: isDark ? "rgba(255,255,255,0.08)" : undefined }} />

            <TextField
              select
              fullWidth
              label="Select Asset"
              value={selectedAssetId}
              onChange={(e) => setSelectedAssetId(e.target.value)}
              error={touched && !selectedAssetId}
              helperText={
                touched && !selectedAssetId
                  ? "Select the asset to mark as defaulted"
                  : assetsLoading
                    ? "Checking eligible assets…"
                    : " "
              }
              disabled={assetsLoading || customerAssets.length === 0}
            >
              {customerAssets.map((asset) => (
                <MenuItem key={asset.Id} value={asset.Id}>
                  {asset.AssetTypeName}
                  {asset.SubTypeName ? ` — ${asset.SubTypeName}` : ""} ·{" "}
                  {String(asset.AssetStatus).replace(/_/g, " ")} ·{" "}
                  {formatCurrency(asset.IndicativeValue)}
                </MenuItem>
              ))}
            </TextField>

            {!assetsLoading && customerAssets.length === 0 && lienBlockedAssets.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No eligible assets found for this customer.
              </Alert>
            )}

            {!assetsLoading && lienBlockedAssets.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {lienBlockedAssets.length === 1
                  ? `${lienBlockedAssets[0].AssetTypeName || "This asset"} cannot be marked as defaulted yet.`
                  : `${lienBlockedAssets.length} asset(s) cannot be marked as defaulted yet.`}{" "}
                The lien must be registered first — go to{" "}
                <strong>Asset Loan Management &gt; Lien Management</strong> to register it.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button onClick={resetAndClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : <ReportProblemIcon />
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            "&:hover": { boxShadow: "0 4px 12px rgba(210,40,54,0.4)" },
            transition: "all 0.18s ease",
          }}
        >
          {submitting ? "Submitting..." : "Initiate Default"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InitiateDefaultDialog;
